import React, {
    useState, useRef, useEffect, useCallback
} from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { useSelector } from 'react-redux';
import { showAddHighlightPillsSelector } from 'viewModel/selectors/testTarget/showAddHighlightPillsSelector';
import {
    Box, Button, Text, Divider, Grid, Image, Link
} from 'components/ui';
import Chevron from 'components/Chevron/Chevron';
import Modal from 'components/Modal/Modal';
import languageLocale from 'utils/LanguageLocale';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import constantsAnalytics from 'analytics/constants';
import CleanHighlightProducts from 'components/ProductPage/CleanHighlightProducts/CleanHighlightProducts';
import {
    buttons, colors, fontSizes, mediaQueries, radii, space
} from 'style/config';
import RwdProductPageEN from 'components/ProductPage/locales/RwdProductPage_en_US';
import RwdProductPageFR from 'components/ProductPage/locales/RwdProductPage_fr_CA';

const {
    PAGE_TYPES: { PRODUCT }
} = constantsAnalytics;
const getText = text => languageLocale.getLocalisedDataFromResource(RwdProductPageEN, RwdProductPageFR)(text);
const cleanAtSephoraID = '900001';
const MAX_ITEMS = 6;
const PILL_IMAGE_SIZE_SM = 20;
const PILL_IMAGE_SIZE_LG = 25;
const PDP_CONTENT_MARGIN_LEFT = 212;
const PDP_CONTENT_MARGIN_RIGHT = 82;

const trackModalShow = item => {
    const pageType = PRODUCT;
    const pageDetail = `${pageType} highlight`;
    const pageWorld = digitalData.page.attributes.world;
    const itemName = item?.name?.toLowerCase();
    const pageName = `${pageType}:${pageDetail}:${pageWorld}:*${itemName}`;
    const data = {
        pageName: pageName,
        pageDetail: pageDetail,
        pageType: pageType,
        world: pageWorld,
        linkData: `${pageDetail}:${itemName}`,
        previousPageName: digitalData.page.attributes.sephoraPageInfo.pageName
    };
    pdpAnalytics.trackPageLoad(data);
};

const Highlights = ({ items: itemsProp = [], itemId, hideTitle = false }) => {
    const { challengerOne, challengerTwo } = useSelector(showAddHighlightPillsSelector);

    const [showModal, setShowModal] = useState(false);
    const [activeItem, setActiveItem] = useState(null);
    const [showMoreProducts, setShowMoreProducts] = useState(false);
    const triggerRef = useRef(null);

    useEffect(() => {
        if (!showModal && triggerRef.current) {
            triggerRef.current.focus();
            triggerRef.current = null;
        }
    }, [showModal]);

    const openModal = useCallback((item, isShowProductsLink) => {
        triggerRef.current = document.activeElement;
        setShowModal(true);
        setActiveItem(item);
        setShowMoreProducts(isShowProductsLink);
        trackModalShow(item);
    }, []);

    const closeModal = useCallback(() => {
        setShowModal(false);
        setActiveItem(null);
        setShowMoreProducts(false);
    }, []);

    const renderPills = (items, withImages) => (
        <Box css={styles.pillsContainer}>
            {items.map(item => (
                <span
                    key={item.id}
                    css={[styles.pill, withImages && styles.pillWithImages, { cursor: item.description ? 'pointer' : 'default' }]}
                    {...(item.description && {
                        role: 'button',
                        tabIndex: 0,
                        'aria-haspopup': 'dialog',
                        onClick: () => openModal(item, item.id === cleanAtSephoraID),
                        onKeyDown: e => {
                            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                                e.preventDefault();
                                openModal(item, item.id === cleanAtSephoraID);
                            }
                        }
                    })}
                >
                    {withImages && (
                        <Image
                            disableLazyLoad={true}
                            src={item.imageUrl}
                            alt=''
                            aria-hidden='true'
                            borderRadius={radii.full}
                            size={[PILL_IMAGE_SIZE_SM, null, PILL_IMAGE_SIZE_LG]}
                        />
                    )}
                    {item.name}
                    {item.description && (
                        <Chevron
                            direction='right'
                            size='.75em'
                            marginLeft='.25em'
                            css={styles.chevron}
                        />
                    )}
                </span>
            ))}
        </Box>
    );

    const allItems = itemsProp.slice(0, MAX_ITEMS);
    // challengerOne renders text-only pills — imageUrl is not required for those items
    const displayItems = challengerOne ? allItems : allItems.filter(item => item.imageUrl);

    if (!displayItems.length) {
        return null;
    }

    return (
        <>
            {!hideTitle && (
                <>
                    <Divider />
                    <Text
                        is='h2'
                        lineHeight='tight'
                        marginTop='1em'
                        marginBottom={5}
                        fontSize={['md', 'lg']}
                        fontWeight='bold'
                        children={getText('highlights')}
                    />
                </>
            )}
            {challengerOne && renderPills(displayItems, false)}
            {challengerTwo && renderPills(displayItems, true)}
            {!challengerOne && !challengerTwo && (
                <Grid
                    columns={[2, 3]}
                    columnGap={[4, 6]}
                    rowGap={[5, 6]}
                    lineHeight='tight'
                    fontSize={['sm', null, 'base']}
                    marginLeft={[null, null, null, PDP_CONTENT_MARGIN_LEFT]}
                    marginRight={[null, null, null, PDP_CONTENT_MARGIN_RIGHT]}
                    marginBottom={[6, 8]}
                >
                    {displayItems.map(item => (
                        <Grid
                            key={item.id}
                            columns='auto 1fr'
                            alignItems='center'
                            gap={[2, null, 3]}
                            {...(item.description && {
                                role: 'button',
                                tabIndex: 0,
                                'aria-haspopup': 'dialog',
                                css: styles.gridItemClickable,
                                onClick: () => openModal(item, false),
                                onKeyDown: e => {
                                    if (e.target !== e.currentTarget) {
                                        return;
                                    }

                                    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                                        e.preventDefault();
                                        openModal(item, false);
                                    }
                                }
                            })}
                        >
                            <Image
                                disableLazyLoad={true}
                                src={item.imageUrl}
                                alt=''
                                aria-hidden='true'
                                borderRadius='full'
                                size={[40, null, 48]}
                            />
                            <Box
                                is='span'
                                display='inline'
                            >
                                <Text css={item.description && styles.hoverUnderline}>{item.name}</Text>
                                {item.description && (
                                    <Text
                                        is='span'
                                        display='inline'
                                        whiteSpace='nowrap'
                                    >
                                        <span aria-hidden='true'>&#xfeff;</span>
                                        <Chevron
                                            direction='right'
                                            size='.75em'
                                            marginLeft='.5em'
                                            css={styles.chevron}
                                        />
                                        {item.id === cleanAtSephoraID && (
                                            <Link
                                                padding={2}
                                                margin={-2}
                                                color='blue'
                                                display='block'
                                                children={getText('showMoreProducts')}
                                                css={styles.hoverUnderline}
                                                onClick={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    openModal(item, true);
                                                }}
                                            />
                                        )}
                                    </Text>
                                )}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Modal
                width={showMoreProducts ? 6 : 2}
                isOpen={showModal}
                isDrawer={true}
                onDismiss={closeModal}
            >
                <Modal.Header isLeftAligned={true}>
                    <Modal.Title>
                        <Grid
                            columns='auto 1fr'
                            alignItems='center'
                            gap={4}
                        >
                            {activeItem?.imageUrl && (
                                <Image
                                    disableLazyLoad={true}
                                    src={activeItem.imageUrl}
                                    alt={activeItem?.altText}
                                    borderRadius='full'
                                    size={40}
                                />
                            )}
                            {activeItem?.name}
                        </Grid>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body paddingBottom={showMoreProducts ? 6 : 4}>
                    {!showMoreProducts && activeItem?.description}
                    {showMoreProducts && (
                        <CleanHighlightProducts
                            closeParentModal={closeModal}
                            itemId={itemId}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer css={showMoreProducts && styles.modalFooterBorder}>
                    <Button
                        onClick={closeModal}
                        variant='primary'
                        hasMinWidth={true}
                        width={['100%', 'auto']}
                        children={getText('gotIt')}
                    />
                </Modal.Footer>
            </Modal>
        </>
    );
};

const styles = {
    pillsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: space[2],
        marginBottom: space[6],
        [mediaQueries.lg]: {
            marginLeft: PDP_CONTENT_MARGIN_LEFT,
            marginRight: PDP_CONTENT_MARGIN_RIGHT
        }
    },
    pill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: space[1],
        paddingLeft: space[2],
        paddingRight: space[2],
        height: buttons.HEIGHT_SM,
        borderRadius: radii.full,
        backgroundColor: colors.nearWhite,
        fontSize: fontSizes.sm,
        whiteSpace: 'nowrap',
        [mediaQueries.lg]: {
            fontSize: fontSizes.base
        }
    },
    pillWithImages: {
        [mediaQueries.lg]: {
            height: '40px'
        }
    },
    chevron: {
        position: 'relative',
        top: '-.0625em'
    },
    hoverUnderline: {
        '.no-touch &:hover': {
            textDecoration: 'underline'
        }
    },
    modalFooterBorder: {
        borderTop: `1px solid ${colors.lightGray}`,
        paddingTop: space[3]
    },
    gridItemClickable: {
        cursor: 'pointer'
    }
};

export default wrapFunctionalComponent(Highlights, 'Highlights');
